import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconCopy, IconDownload, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { TrizState } from "../api/types";
import { usePrinciples } from "../hooks/usePrinciples";
import { downloadMarkdown, stateToMarkdown } from "../utils/exportMarkdown";

interface RunActionsProps {
  state: TrizState;
}

/**
 * Панель действий над одним прогоном: поделиться ссылкой, экспортировать
 * как markdown, удалить. Стоит в шапке RunPage.
 */
export function RunActions({ state }: RunActionsProps) {
  const clipboard = useClipboard({ timeout: 1500 });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: principles } = usePrinciples();

  const onShare = () => {
    const url = `${window.location.origin}/runs/${state.task_id}`;
    clipboard.copy(url);
    notifications.show({
      title: "Ссылка скопирована",
      message: url,
      color: "teal",
      icon: <IconCheck size={16} />,
      autoClose: 2000,
    });
  };

  const onExport = () => {
    const md = stateToMarkdown(state, principles);
    const dateStamp = new Date(state.created_at)
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    downloadMarkdown(md, `metatriz-${dateStamp}.md`);
    notifications.show({
      title: "Экспорт готов",
      message: "Markdown-файл скачан",
      color: "blue",
      autoClose: 2000,
    });
  };

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteRun(state.task_id),
    onSuccess: () => {
      notifications.show({
        title: "Прогон удалён",
        message: "Запись убрана из истории",
        color: "gray",
        autoClose: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["runs"] });
      navigate("/", { replace: true });
    },
    onError: (err) => {
      notifications.show({
        title: "Не удалось удалить",
        message: String(err),
        color: "red",
      });
    },
  });

  const onDelete = () => {
    if (window.confirm("Удалить этот прогон из истории?")) {
      deleteMutation.mutate();
    }
  };

  return (
    <Group gap="xs">
      <Tooltip label="Скопировать ссылку">
        <ActionIcon variant="default" onClick={onShare} aria-label="Поделиться">
          <IconCopy size={18} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Скачать как Markdown">
        <ActionIcon variant="default" onClick={onExport} aria-label="Экспорт markdown">
          <IconDownload size={18} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Удалить прогон">
        <ActionIcon
          variant="default"
          color="red"
          onClick={onDelete}
          loading={deleteMutation.isPending}
          aria-label="Удалить"
        >
          <IconTrash size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
